/**
 * Backend Configuration
 * 
 * Since frontend and backend are now served from the same instance,
 * we use same-origin URLs (relative URLs) for all backend communication.
 */

// Organization slug to backend URL mapping
// This can be used for custom mappings if needed for special cases
const ORGANIZATION_BACKENDS = {
  // Add custom organization mappings here if needed
  // Example: 'custom-org': 'https://custom-backend.example.com',
};

/**
 * Get backend URL for a specific organization
 * 
 * @param {string} orgSlug - Organization slug from Clerk (optional, kept for compatibility)
 * @returns {string} Backend URL - uses same-origin since frontend and backend are co-located
 */
export const getBackendUrl = (orgSlug) => {
  // Check environment variable first
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }

  // Check if we have a custom backend mapping for this organization
  if (orgSlug && ORGANIZATION_BACKENDS[orgSlug]) {
    return ORGANIZATION_BACKENDS[orgSlug];
  }

  // Production: Frontend and backend are on the same domain
  // Use same-origin (relative URL would also work, but explicit is clearer)
  return window.location.origin;
};

/**
 * Get WebSocket URL for a specific organization
 * 
 * @param {string} orgSlug - Organization slug from Clerk
 * @returns {string} WebSocket URL for the organization
 */
export const getWebSocketUrl = (orgSlug) => {
  const backendUrl = getBackendUrl(orgSlug);
  
  // Convert HTTP/HTTPS to WS/WSS
  if (backendUrl.startsWith('https://')) {
    return backendUrl.replace('https://', 'wss://');
  } else if (backendUrl.startsWith('http://')) {
    return backendUrl.replace('http://', 'ws://');
  }
  
  return backendUrl;
};

/**
 * Get API base URL for a specific organization
 * 
 * @param {string} orgSlug - Organization slug from Clerk
 * @returns {string} API base URL for the organization
 */
export const getApiBaseUrl = (orgSlug) => {
  const backendUrl = getBackendUrl(orgSlug);
  
  // Ensure the URL ends with /api
  if (backendUrl.endsWith('/api')) {
    return backendUrl;
  } else if (backendUrl.endsWith('/')) {
    return `${backendUrl}api`;
  } else {
    return `${backendUrl}/api`;
  }
};

/**
 * Check if we're in development mode
 * 
 * @returns {boolean} True if in development mode
 */
export const isDevelopment = () => {
  return process.env.NODE_ENV === 'development' || 
         window.location.hostname === 'localhost';
};

/**
 * Get the current organization slug from Clerk
 * This is a helper function that can be used in components
 * 
 * @param {object} organization - Clerk organization object
 * @returns {string|null} Organization slug or null
 */
export const getOrgSlug = (organization) => {
  if (!organization) return null;
  
  // Clerk organization slug is typically in the slug property
  return organization.slug || organization.id;
};

const backendConfig = {
  getBackendUrl,
  getWebSocketUrl,
  getApiBaseUrl,
  isDevelopment,
  getOrgSlug,
  ORGANIZATION_BACKENDS
};

export default backendConfig;
